import axios from 'utils/axios'
import { useMutation } from 'react-query'
import hash from 'object-hash'
import { StoreProfitEvents } from '@merchx-v2/shared-types'

import { guardFromErrors, extractErrorInfo } from 'utils/graphqlHelpers'
import { queryClient } from 'queryClient'

/// ////////////////////////////////////////////////////////////////////////////////
// GRAPHQL
/// ////////////////////////////////////////////////////////////////////////////////

type QueryResponse = GraphQLResponse<'increaseBalance', boolean>

const INCREASE_BALANCE = `
  mutation IncreaseBalance ($storeId: Int!, $sum: Int!, $note: String!, $event: StoreProfitEvent!, $payload: String) {
    increaseBalance(storeId: $storeId, sum: $sum, note: $note, event: $event, payload: $payload)
  }
`

/// ////////////////////////////////////////////////////////////////////////////////
// FUNCTION
/// ////////////////////////////////////////////////////////////////////////////////

type IncreaseBalanceArgs = {
  storeId: number
  sum: number
  note: string
  event: StoreProfitEvents
  payload?: string
}

type IncreaseBalance = (args: IncreaseBalanceArgs) => Promise<IncreaseBalanceArgs>

const increaseBalance: IncreaseBalance = async (args) => {
  try {
    const {
      data: { data, errors }
    }: QueryResponse = await axios.post('/graphql', {
      query: INCREASE_BALANCE,
      variables: args
    })

    guardFromErrors(errors)

    if (!data) {
      throw new Error('Response body is empty!')
    }

    const { increaseBalance } = data
    if (!increaseBalance) {
      throw new Error("Can't increase store balance!")
    }

    return args
  } catch (err) {
    throw new Error(extractErrorInfo(err) as string)
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK EVENTS
/// ////////////////////////////////////////////////////////////////////////////////

type IncreaseBalanceEvents = {
  onSettled: (data?: IncreaseBalanceArgs) => void
}

const increaseBalanceEvents: IncreaseBalanceEvents = {
  onSettled: (data) => {
    if (data?.storeId) {
      queryClient.invalidateQueries(['storeProfitsPage', hash({ storeId: data.storeId })])
    }
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK
/// ////////////////////////////////////////////////////////////////////////////////

export const useIncreaseBalance = () => useMutation(increaseBalance, increaseBalanceEvents)
